import type { ApiMaterial, ApiProfileRef, InventoryProfileType } from '@/types/inventory'

/** Metadata for each of the 12 profile categories: label + the Material relation/FK it maps to. */
export interface ProfileTypeMeta {
  type: InventoryProfileType
  label: string
  relation: keyof ApiMaterial
  fkField: string
}

export const PROFILE_TYPE_META: ProfileTypeMeta[] = [
  { type: 'material_description', label: 'Material Description', relation: 'materialDescriptionProfile', fkField: 'materialDescriptionProfileId' },
  { type: 'manufacturer', label: 'Manufacturer', relation: 'manufacturerProfile', fkField: 'manufacturerProfileId' },
  { type: 'inventory_unit', label: 'Inventory Unit', relation: 'inventoryUnitProfile', fkField: 'inventoryUnitProfileId' },
  { type: 'storage', label: 'Storage Profile', relation: 'storageProfile', fkField: 'storageProfileId' },
  { type: 'traceability', label: 'Traceability', relation: 'traceabilityProfile', fkField: 'traceabilityProfileId' },
  { type: 'shelf_life', label: 'Shelf Life Days', relation: 'shelfLifeProfile', fkField: 'shelfLifeProfileId' },
  { type: 'quality', label: 'Quality Status', relation: 'qualityProfile', fkField: 'qualityProfileId' },
  { type: 'usage', label: 'Usage', relation: 'usageProfile', fkField: 'usageProfileId' },
  { type: 'regulatory', label: 'Regulatory', relation: 'regulatoryProfile', fkField: 'regulatoryProfileId' },
  { type: 'cost', label: 'Cost', relation: 'costProfile', fkField: 'costProfileId' },
  { type: 'attachments', label: 'Attachments', relation: 'attachmentsProfile', fkField: 'attachmentsProfileId' },
]

export const PROFILE_TYPE_LABEL: Record<InventoryProfileType, string> = {
  ...Object.fromEntries(PROFILE_TYPE_META.map((m) => [m.type, m.label])),
  // actions + status are config-only categories (no Material FK link)
  actions: 'Actions',
  status: 'Status',
  // Retired as a profile category — product codes are now columns on the
  // material itself (`primaryProductCode` / `productCodes`), captured when the
  // material is created. The label is kept so any historical reference to the
  // enum value still renders a name instead of `undefined`.
  product_code: 'Product Code',
} as Record<InventoryProfileType, string>

/** A category's colour, in the two forms the app needs it. */
export interface ProfileTypeColor {
  /** Chip / badge classes: tinted surface, readable ink, matching hairline. */
  chip: string
  /** The hue on its own, for a bar, a dot or a hyperlink. */
  ink: string
  /** ARGB fill for an ExcelJS header cell, which cannot take a Tailwind class. */
  argb: string
  /** ARGB for the text sitting on that fill. */
  inkArgb: string
}

/**
 * One colour per master-data category, used everywhere that category appears.
 *
 * Fourteen hues is past the point where colour alone identifies anything, so it
 * is deliberately not asked to: every chip keeps its icon and its label, and the
 * colour does the cheaper job of letting the eye group and re-find a category
 * across the config tabs, the material table and the import wizard.
 *
 * The hues are ordered so that categories adjacent in the tab row never sit on
 * neighbouring hues, and two of them are grounded rather than arbitrary:
 * `storage` is the cold-chain blue this industry already reads as temperature,
 * and the two categories that are not live config — retired `product_code` and
 * `attachments`, which holds files rather than a domain concept — are the only
 * desaturated entries, so "this is not a category like the others" is carried by
 * the colour itself.
 *
 * Typed as a total `Record`, so adding a value to `InventoryProfileType` without
 * giving it a colour fails `pnpm lint` instead of rendering `undefined`.
 */
export const PROFILE_TYPE_COLOR: Record<InventoryProfileType, ProfileTypeColor> = {
  material_description: {
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    ink: 'text-indigo-700',
    argb: 'FFE0E7FF',
    inkArgb: 'FF3730A3',
  },
  manufacturer: {
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    ink: 'text-amber-700',
    argb: 'FFFEF3C7',
    inkArgb: 'FF92400E',
  },
  inventory_unit: {
    chip: 'bg-teal-50 text-teal-700 border-teal-200',
    ink: 'text-teal-700',
    argb: 'FFCCFBF1',
    inkArgb: 'FF115E59',
  },
  storage: {
    chip: 'bg-sky-50 text-sky-700 border-sky-200',
    ink: 'text-sky-700',
    argb: 'FFE0F2FE',
    inkArgb: 'FF075985',
  },
  traceability: {
    chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    ink: 'text-fuchsia-700',
    argb: 'FFFAE8FF',
    inkArgb: 'FF86198F',
  },
  shelf_life: {
    chip: 'bg-orange-50 text-orange-700 border-orange-200',
    ink: 'text-orange-700',
    argb: 'FFFFEDD5',
    inkArgb: 'FF9A3412',
  },
  quality: {
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ink: 'text-emerald-700',
    argb: 'FFD1FAE5',
    inkArgb: 'FF065F46',
  },
  usage: {
    chip: 'bg-lime-50 text-lime-700 border-lime-200',
    ink: 'text-lime-700',
    argb: 'FFECFCCB',
    inkArgb: 'FF3F6212',
  },
  regulatory: {
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
    ink: 'text-blue-700',
    argb: 'FFDBEAFE',
    inkArgb: 'FF1E40AF',
  },
  cost: {
    chip: 'bg-rose-50 text-rose-700 border-rose-200',
    ink: 'text-rose-700',
    argb: 'FFFFE4E6',
    inkArgb: 'FF9F1239',
  },
  attachments: {
    chip: 'bg-stone-50 text-stone-700 border-stone-200',
    ink: 'text-stone-700',
    argb: 'FFF5F5F4',
    inkArgb: 'FF44403C',
  },
  actions: {
    chip: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    ink: 'text-cyan-700',
    argb: 'FFCFFAFE',
    inkArgb: 'FF155E75',
  },
  status: {
    chip: 'bg-violet-50 text-violet-700 border-violet-200',
    ink: 'text-violet-700',
    argb: 'FFEDE9FE',
    inkArgb: 'FF5B21B6',
  },
  // Retired as a category (see PROFILE_TYPE_LABEL), but still keyed: a tenant
  // that predates the change can hold product_code profiles, and the import
  // schema reports them.
  product_code: {
    chip: 'bg-slate-50 text-slate-600 border-slate-200',
    ink: 'text-slate-600',
    argb: 'FFF1F5F9',
    inkArgb: 'FF334155',
  },
}

/** The colour tokens for a category. */
export function profileTypeColor(type: InventoryProfileType): ProfileTypeColor {
  return PROFILE_TYPE_COLOR[type]
}

/**
 * The storage-profile field that carries the validated storage condition.
 *
 * One key on both sides of a put-away check: a material points at a storage
 * profile for what it REQUIRES, and a location points at one for what it
 * PROVIDES. Mirrors `MATERIAL_CONDITION_KEY` in the backend.
 */
export const STORAGE_CONDITION_FIELD = 'storageCondition'

/**
 * The condition a storage profile names, or null.
 *
 * Null covers every way of not naming one — no entry, switched off, or empty —
 * because the fields map is free-form JSON that predates this field and a
 * profile written before it must read as "says nothing", not crash the picker.
 */
export function profileCondition(profile: {
  fields?: Record<string, { active?: boolean; value?: unknown }> | null
}): string | null {
  const entry = profile.fields?.[STORAGE_CONDITION_FIELD]
  if (!entry || entry.active === false) return null
  return typeof entry.value === 'string' && entry.value !== '' ? entry.value : null
}

/** The named profile currently assigned to a material for a given category (or null). */
export function materialProfileRef(
  material: ApiMaterial,
  type: InventoryProfileType,
): ApiProfileRef | null {
  const meta = PROFILE_TYPE_META.find((m) => m.type === type)
  if (!meta) return null
  return (material[meta.relation] as ApiProfileRef | null | undefined) ?? null
}

/** The Material FK field name for assigning a profile of this category. */
export function profileFkField(type: InventoryProfileType): string {
  return PROFILE_TYPE_META.find((m) => m.type === type)?.fkField ?? ''
}
